import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/base/SEOHead';
import { getArticlesByCategory } from '@/mocks/articles';
import { getCategoryBySlug, categories } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryArticles = getArticlesByCategory(slug || '');

  const pageTitle = category
    ? `${category.name} - 51每日大赛 | 网红明星塌房现场猛料实时追踪`
    : '分类页面 - 51每日大赛';

  const pageDescription = category
    ? `${category.description} 51每日大赛官网每日更新吃瓜，实时追踪网红明星塌房现场猛料。`
    : '51每日大赛分类页面，浏览各类网红明星塌房爆料内容。';

  const pageSchema = category
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name} - 51每日大赛`,
        description: category.description,
        url: `http://chiguaji.cn/category/${category.slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '51每日大赛',
          url: 'http://chiguaji.cn/',
        },
      }
    : undefined;

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到 - 51每日大赛" description="抱歉，您访问的分类页面不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-3xl font-black text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的分类页面不存在。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`51每日大赛,${category.name},网红明星塌房,每日更新吃瓜,实时追踪`}
        canonicalUrl={`http://chiguaji.cn/category/${category.slug}`}
        schema={pageSchema}
      />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20with%20deep%20red%20and%20dark%20charcoal%20flowing%20waves%2C%20modern%20artistic%20background%2C%20subtle%20texture%2C%20dramatic%20atmosphere%2C%20minimal%20composition%2C%20elegant%20dark%20aesthetic&width=1800&height=400&seq=category-hero-generic-01&orientation=landscape"
            alt={`51每日大赛 - ${category.name}`}
            title={`51每日大赛${category.name}频道`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/60"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-white text-3xl md:text-5xl font-black">{category.name}</h1>
                <p className="text-white/70 text-sm md:text-base mt-2 max-w-xl">
                  共 {category.articleCount} 篇爆料
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb + Content */}
        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex items-center gap-3 py-4 overflow-x-auto flex-nowrap border-b border-background-200 mb-8">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分类热榜</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-14">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-lg">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-900 mb-8">
              相关分类推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories
                .filter((c) => c.slug !== category.slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="block bg-background-50 rounded-xl p-6 hover:shadow-sm transition-all border border-background-200 hover:border-primary-200 cursor-pointer"
                  >
                    <h3 className="text-foreground-900 font-bold text-lg mb-2">{cat.name}</h3>
                    <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{cat.description}</p>
                    <span className="text-primary-500 text-sm font-medium">{cat.articleCount} 篇内容</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}